// src/components/research/comparison.js

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

/* Positioning against neighbouring work, as rows rather than prose.

   Two reasons it is a component and not a paragraph. First, a reader deciding
   whether this is serious checks exactly one thing — whether the author knows
   what already exists — and a scannable list answers that in seconds where a
   paragraph makes them hunt. Second, the shape enforces honesty: every row has
   to name a real point of contact before it is allowed to claim a difference,
   so there is nowhere to put a straw man.

   Not a <table>. The content is a set of short relationships, and a table at
   this width forces either a horizontal scroll or three cramped columns; rows
   that stack cleanly say the same thing at any size. */
const StyledComparison = styled.ul`
  ${({ theme }) => theme.mixins.resetList};
  margin-top: var(--space-5);

  li {
    display: grid;
    /* The name is two or three short words and wraps happily; the relations
       are sentences and were being squeezed into four and five lines. */
    grid-template-columns: minmax(96px, 132px) 1fr;
    grid-gap: var(--space-4);
    padding: var(--space-4) 0;
    border-top: 1px solid var(--border);

    &:last-child {
      border-bottom: 1px solid var(--border);
    }

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
      grid-gap: var(--space-2);
    }
  }

  .name {
    color: var(--text-emphasis);
    font-family: var(--font-sans);
    font-size: var(--fz-lg);
    font-weight: 500;
    line-height: var(--lh-body);
  }

  /* A two-track grid rather than an inline-block label. With the label inline
     the second line of a wrapping value runs back to the paragraph edge and
     sits underneath the label, so the value has no left edge of its own. The
     grid gives it one, and the labels stay in a column the eye can run down. */
  .relation {
    display: grid;
    grid-template-columns: 62px 1fr;
    grid-gap: var(--space-2);
    margin: 0;
    color: var(--text-body);
    font-size: var(--fz-lg);
    line-height: var(--lh-body);

    .tag {
      /* Nudged onto the value's first baseline: the label is several points
         smaller, so aligning the two line boxes leaves it riding high. */
      padding-top: 0.25em;
      color: var(--text-muted);
      font-size: var(--fz-xs);
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    & + .relation {
      margin-top: var(--space-2);
    }
  }
`;

const Comparison = ({ rows, className }) => (
  <StyledComparison className={className}>
    {rows.map(({ name, shares, differs }) => (
      <li key={name}>
        <span className="name">{name}</span>
        <div>
          <p className="relation">
            <span className="tag">Shares</span>
            <span>{shares}</span>
          </p>
          <p className="relation">
            <span className="tag">Differs</span>
            <span>{differs}</span>
          </p>
        </div>
      </li>
    ))}
  </StyledComparison>
);

Comparison.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      shares: PropTypes.node.isRequired,
      differs: PropTypes.node.isRequired,
    }),
  ).isRequired,
  className: PropTypes.string,
};

export default Comparison;
